import { useTranslation } from 'react-i18next';
import RingSizeGuide, { RING_SIZES } from './RingSizeGuide';

export type SizeStock = 'in_stock' | 'made_to_order' | 'out_of_stock';

export interface SizeInventory {
  [euSize: number]: SizeStock;
}

interface RingSizeSelectorProps {
  selectedSize: string;
  onSizeChange: (size: string) => void;
  isAdjustable?: boolean;
  /** Per-size stock status. If not provided, all sizes shown as available. */
  sizeInventory?: SizeInventory;
}

const RingSizeSelector = ({
  selectedSize,
  onSizeChange,
  isAdjustable,
  sizeInventory,
}: RingSizeSelectorProps) => {
  const { t } = useTranslation();

  // Adjustable rings don't need a size selector
  if (isAdjustable) {
    return (
      <div className="mb-6">
        <div className="flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <span className="text-green-700 dark:text-green-400 font-semibold text-sm">
            {t('ringSize.adjustable')}
          </span>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
          {t('ringSize.adjustableHint')}
        </p>
      </div>
    );
  }

  const getStockLabel = (euSize: number): string => {
    if (!sizeInventory) return '';
    const status = sizeInventory[euSize];
    if (status === 'in_stock') return ` — ${t('product.inStock')}`;
    if (status === 'made_to_order') return ` — ${t('product.madeToOrder')}`;
    if (status === 'out_of_stock') return ` — ${t('product.outOfStock')}`;
    return '';
  };

  const isDisabled = (euSize: number): boolean => {
    if (!sizeInventory) return false;
    return sizeInventory[euSize] === 'out_of_stock';
  };

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <label className="block text-black dark:text-white">
          {t('ringSize.selectSize')}
        </label>
        <RingSizeGuide />
      </div>
      <select
        className="w-full p-2 border rounded-lg bg-white dark:bg-brand-charcoal text-black dark:text-white"
        value={selectedSize}
        onChange={(e) => onSizeChange(e.target.value)}
      >
        <option value="">{t('ringSize.selectSizePlaceholder')}</option>
        {RING_SIZES.map((size) => (
          <option
            key={size.eu}
            value={`EU ${size.eu}`}
            disabled={isDisabled(size.eu)}
          >
            EU {size.eu} ({size.mm.toFixed(1)} mm){getStockLabel(size.eu)}
          </option>
        ))}
      </select>
    </div>
  );
};

export default RingSizeSelector;
